import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/autoplay";
import ThreeModelViewer from "../ThreeModelViewer";
import { FiGrid, FiList, FiTable } from "react-icons/fi";
import { FaTag } from "react-icons/fa";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
const fallbackImageUrl = "/default-product.jpg";

const buildImageUrl = (filename) => {
  if (!filename || typeof filename !== "string") return "";

  // URL complète détectée, on la retourne brute
  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  // Sinon on construit URL locale
  let cleanFilename = filename.startsWith("/") ? filename.slice(1) : filename;

  if (cleanFilename.startsWith("public/")) {
    cleanFilename = cleanFilename.slice("public/".length);
  }

  if (!cleanFilename.startsWith("uploads/")) {
    cleanFilename = `uploads/${cleanFilename}`;
  }

  return `${apiUrl}/${cleanFilename}`.replace(/([^:]\/)\/+/g, "$1");
};

const normalize = (str) => (str ? str.trim().toLowerCase() : "");

const getVariantByColorAndTaille = (product, couleur, taille) => {
  if (!product?.variantes) return null;
  return product.variantes.find(
    (v) =>
      normalize(v.couleur) === normalize(couleur) &&
      v.stock?.some((s) => normalize(s.taille) === normalize(taille))
  );
};

const formatPrice = (price) => {
  if (price === undefined || price === null) return "0.00";
  return parseFloat(price).toFixed(2);
};

const PopularProducts = () => {
  const [products, setProducts] = useState([]);
  const [couleur] = useState("noir");
  const [taille] = useState("40");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("swiper");
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchPopularProducts = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/shop/products/populaires`);
        setProducts(response.data.products || []);
      } catch (err) {
        console.error("Erreur lors du chargement :", err);
        setError("Erreur lors du chargement des produits populaires");
      } finally {
        setLoading(false);
      }
    };

    fetchPopularProducts();
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Sur mobile, on force le mode swiper pour une meilleure expérience
      if (mobile && (viewMode === "grid" || viewMode === "table")) {
        setViewMode("swiper");
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, [viewMode]);

  // Fonction pour vérifier si le tableau peut être affiché
  const canShowTable = () => {
    return !isMobile && products.length > 0;
  };

  const renderProductCard = (product, index) => {
    const variante = getVariantByColorAndTaille(product, couleur, taille) || product.variantes?.[0];
    const firstImage = variante?.images?.[0];
    const is3D = firstImage?.match(/\.(glb|gltf|obj)$/i);
    const imageUrl = buildImageUrl(firstImage);
    const hasPromo = product.price?.promoPrice && product.price.promoPrice < product.price.originalPrice;
    const originalPrice = product.price?.originalPrice || 0;
    const promoPrice = product.price?.promoPrice || originalPrice;
    const promoPercentage = product.promo || 0;

    return (
      <motion.div
        key={product._id}
        onClick={() => navigate(`/product/${product._id}`)}
        whileHover={{ y: -5 }}
        style={{
          borderRadius: "20px",
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(212, 175, 55, 0.2)",
          overflow: "hidden",
          cursor: "pointer",
          height: "100%",
          transition: "all 0.3s ease",
          position: "relative",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)"
        }}
      >
        {product.isBestSeller && (
          <div 
            style={{
              position: "absolute",
              top: "12px",
              left: "12px",
              background: "rgba(212, 175, 55, 0.9)",
              color: "#2c1810",
              padding: "6px 12px",
              borderRadius: "15px",
              fontSize: "10px",
              fontWeight: "600",
              zIndex: "2",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              boxShadow: "0 4px 12px rgba(212, 175, 55, 0.3)"
            }}
          >
            Best-seller
          </div>
        )}

        <div style={{
          width: "100%",
          height: "220px",
          overflow: "hidden",
          position: "relative"
        }}>
          {is3D ? (
            <ThreeModelViewer url={imageUrl} width="100%" height={220} />
          ) : (
            <img
              src={imageUrl}
              alt={product.name || "Image produit"}
              style={{ 
                width: "100%", 
                height: "100%", 
                objectFit: "cover",
                transition: "transform 0.3s ease"
              }}
              onError={(e) => {
                e.currentTarget.src = fallbackImageUrl;
              }}
            />
          )}
        </div>

        <div style={{ 
          padding: "20px",
          background: "rgba(255, 255, 255, 0.05)"
        }}>
          <h3 style={{
            margin: "0 0 10px",
            fontSize: "16px",
            fontWeight: "600",
            color: "#2c1810",
            lineHeight: "1.3",
            minHeight: "42px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            fontFamily: "'Cormorant Garamond', serif"
          }}>
            {product.name}
          </h3>
          
          <div style={{ 
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            marginBottom: "12px"
          }}>
            {hasPromo && (
              <div style={{ 
                fontSize: "14px", 
                color: "#95a5a6",
                textDecoration: "line-through",
                fontWeight: "500"
              }}>
                {formatPrice(originalPrice)} MAD
              </div>
            )}
            
            <div style={{ 
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <span style={{ 
                fontSize: "18px", 
                fontWeight: "700", 
                color: hasPromo ? "#ef4444" : "#d4af37",
                fontFamily: "'Cormorant Garamond', serif"
              }}>
                {formatPrice(hasPromo ? promoPrice : originalPrice)} MAD
              </span>
              
              {hasPromo && (
                <span style={{
                  background: "#ef4444",
                  color: "white",
                  padding: "2px 6px",
                  borderRadius: "10px",
                  fontSize: "12px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "3px"
                }}>
                  <FaTag size={10} />
                  {promoPercentage}%
                </span>
              )}
            </div>
          </div>
          
          <div style={{ 
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            gap: "8px"
          }}>
            <div style={{ 
              fontSize: "17px", 
              color: "#2c1810",
              background: "rgba(212, 175, 55, 0.15)",
              padding: "4px 10px",
              borderRadius: "12px",
              fontWeight: "500",
              border: "1px solid rgba(212, 175, 55, 0.2)"
            }}>
              {product.type}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderProductList = (product, index) => {
    const variante = getVariantByColorAndTaille(product, couleur, taille) || product.variantes?.[0];
    const firstImage = variante?.images?.[0];
    const is3D = firstImage?.match(/\.(glb|gltf|obj)$/i);
    const imageUrl = buildImageUrl(firstImage);
    const hasPromo = product.price?.promoPrice && product.price.promoPrice < product.price.originalPrice;
    const originalPrice = product.price?.originalPrice || 0;
    const promoPrice = product.price?.promoPrice || originalPrice;
    const promoPercentage = product.promo || 0;

    return (
      <motion.div
        key={product._id}
        onClick={() => navigate(`/product/${product._id}`)}
        whileHover={{ x: 5 }}
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          background: "rgba(255, 255, 255, 0.1)",
          borderRadius: "15px",
          overflow: "hidden",
          cursor: "pointer",
          marginBottom: "15px",
          transition: "all 0.3s ease",
          position: "relative",
          border: "1px solid rgba(212, 175, 55, 0.2)",
          backdropFilter: "blur(20px)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)"
        }}
      >
        <div style={{
          width: isMobile ? "100%" : "180px",
          height: isMobile ? "200px" : "120px",
          overflow: "hidden",
          position: "relative",
          flexShrink: 0
        }}>
          {is3D ? (
            <ThreeModelViewer url={imageUrl} width="180px" height={120} />
          ) : (
            <img
              src={imageUrl}
              alt={product.name || "Image produit"}
              style={{ 
                width: "100%", 
                height: "100%", 
                objectFit: "cover",
                transition: "transform 0.3s ease"
              }}
              onError={(e) => {
                e.currentTarget.src = fallbackImageUrl;
              }}
            />
          )}
          
          {product.isBestSeller && (
            <div style={{
              position: "absolute",
              top: "6px",
              left: "6px",
              background: "rgba(212, 175, 55, 0.9)",
              color: "#2c1810",
              padding: "4px 8px",
              borderRadius: "10px",
              fontSize: "9px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}>
              Best-seller
            </div>
          )}
        </div>

        <div style={{ 
          padding: "15px", 
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }}>
          <div>
            <h3 style={{
              margin: "0 0 6px",
              fontSize: isMobile ? "18px" : "16px",
              fontWeight: "600",
              color: "#2c1810",
              lineHeight: "1.3",
              fontFamily: "'Cormorant Garamond', serif"
            }}>
              {product.name}
            </h3>
            
            <p style={{
              margin: "0 0 10px",
              fontSize: isMobile ? "14px" : "13px",
              color: "#7f8c8d",
              lineHeight: "1.4",
              display: isMobile ? "none" : "block"
            }}>
              {product.description?.substring(0, 80)}...
            </p>
          </div>
          
          <div style={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "10px" : "0"
          }}>
            <div style={{ 
              display: "flex",
              flexDirection: "column",
              gap: "2px"
            }}>
              {hasPromo && (
                <span style={{ 
                  fontSize: isMobile ? "14px" : "12px", 
                  color: "#95a5a6",
                  textDecoration: "line-through"
                }}>
                  {formatPrice(originalPrice)} MAD
                </span>
              )}
              
              <div style={{ 
                display: "flex",
                alignItems: "center",
                gap: "5px"
              }}>
                <span style={{ 
                  fontSize: isMobile ? "20px" : "18px", 
                  fontWeight: "700", 
                  color: hasPromo ? "#ef4444" : "#d4af37",
                  fontFamily: "'Cormorant Garamond', serif"
                }}>
                  {formatPrice(hasPromo ? promoPrice : originalPrice)} MAD
                </span>
                
                {hasPromo && (
                  <span style={{
                    background: "#ef4444",
                    color: "white",
                    padding: "2px 5px",
                    borderRadius: "8px",
                    fontSize: isMobile ? "12px" : "10px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "2px"
                  }}>
                    <FaTag size={isMobile ? 10 : 8} />
                    {promoPercentage}%
                  </span>
                )}
              </div>
            </div>
            
            <div style={{ 
              fontSize: isMobile ? "13px" : "11px", 
              color: "#2c1810",
              background: "rgba(212, 175, 55, 0.15)",
              padding: isMobile ? "6px 12px" : "4px 8px",
              borderRadius: "10px",
              fontWeight: "500",
              border: "1px solid rgba(212, 175, 55, 0.2)"
            }}>
              {product.type}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const renderProductTable = (product, index) => {
    const variante = getVariantByColorAndTaille(product, couleur, taille) || product.variantes?.[0];
    const firstImage = variante?.images?.[0];
    const is3D = firstImage?.match(/\.(glb|gltf|obj)$/i);
    const imageUrl = buildImageUrl(firstImage);
    const hasPromo = product.price?.promoPrice && product.price.promoPrice < product.price.originalPrice;
    const originalPrice = product.price?.originalPrice || 0;
    const promoPrice = product.price?.promoPrice || originalPrice;
    const promoPercentage = product.promo || 0;

    return (
      <tr
        key={product._id}
        onClick={() => navigate(`/product/${product._id}`)}
        style={{
          cursor: "pointer",
          transition: "all 0.3s ease",
          borderBottom: "1px solid rgba(212, 175, 55, 0.1)"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(212, 175, 55, 0.15)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        <td style={{ padding: "12px", textAlign: "center" }}>
          <div style={{
            position: "relative",
            display: "inline-block"
          }}>
            {is3D ? (
              <div style={{
                width: "60px",
                height: "60px",
                borderRadius: "10px",
                overflow: "hidden",
                background: "rgba(255, 255, 255, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "8px",
                color: "#2c1810",
                fontWeight: "600"
              }}>
                3D
              </div>
            ) : (
              <img
                src={imageUrl || fallbackImageUrl}
                alt={product.name || "Image produit"}
                style={{
                  width: "60px",
                  height: "60px",
                  objectFit: "cover",
                  borderRadius: "10px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}
                onError={(e) => {
                  e.currentTarget.src = fallbackImageUrl;
                }}
              />
            )}
            {product.isBestSeller && (
              <div style={{
                position: "absolute",
                top: "-3px",
                right: "-3px",
                background: "rgba(212, 175, 55, 0.9)",
                color: "#2c1810",
                padding: "2px 6px",
                borderRadius: "8px",
                fontSize: "8px",
                fontWeight: "600",
                textTransform: "uppercase"
              }}>
                Best
              </div>
            )}
          </div>
        </td>
        <td style={{ padding: "12px", fontWeight: "600", color: "#2c1810" }}>
          <div>
            <div style={{ fontSize: "13px", fontWeight: "600", marginBottom: "3px", fontFamily: "'Cormorant Garamond', serif" }}>
              {product.name || "Nom non disponible"}
            </div>
            <div style={{ fontSize: "11px", color: "#7f8c8d" }}>
              {product.description ? `${product.description.substring(0, 40)}...` : "Aucune description"}
            </div>
          </div>
        </td>
        <td style={{ padding: "12px", textAlign: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            {hasPromo && (
              <div style={{ 
                fontSize: "11px", 
                color: "#95a5a6",
                textDecoration: "line-through"
              }}>
                {formatPrice(originalPrice)} MAD
              </div>
            )}
            
            <div style={{ 
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px"
            }}>
              <span style={{
                fontSize: "14px",
                fontWeight: "700",
                color: hasPromo ? "#ef4444" : "#d4af37",
                fontFamily: "'Cormorant Garamond', serif"
              }}>
                {formatPrice(hasPromo ? promoPrice : originalPrice)} MAD
              </span>
              
              {hasPromo && (
                <span style={{
                  background: "#ef4444",
                  color: "white",
                  padding: "2px 4px",
                  borderRadius: "6px",
                  fontSize: "9px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "2px"
                }}>
                  <FaTag size={8} />
                  {promoPercentage}%
                </span>
              )}
            </div>
          </div>
        </td>
        <td style={{ padding: "12px", textAlign: "center" }}>
          <div style={{
            fontSize: "10px",
            color: "#2c1810",
            background: "rgba(212, 175, 55, 0.15)",
            padding: "4px 8px",
            borderRadius: "8px",
            fontWeight: "500",
            border: "1px solid rgba(212, 175, 55, 0.2)",
            display: "inline-block"
          }}>
            {product.type || "N/A"}
          </div>
        </td>
        <td style={{ padding: "12px", textAlign: "center" }}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/product/${product._id}`);
            }}
            style={{
              padding: "6px 12px",
              background: "rgba(212, 175, 55, 0.2)",
              color: "#2c1810",
              border: "1px solid rgba(212, 175, 55, 0.3)",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "10px",
              fontWeight: "600",
              transition: "all 0.3s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(212, 175, 55, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "rgba(212, 175, 55, 0.2)";
            }}
          >
            Voir
          </button>
        </td>
      </tr>
    );
  };

  if (loading) return (
    <div style={{ 
      display: "flex", 
      justifyContent: "center", 
      alignItems: "center", 
      height: "200px",
      background: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(20px)",
      borderRadius: "20px",
      margin: "40px auto",
      maxWidth: "1140px",
      border: "1px solid rgba(212, 175, 55, 0.2)"
    }}>
      <div style={{
        width: "40px",
        height: "40px",
        border: "3px solid rgba(212, 175, 55, 0.3)",
        borderTop: "3px solid #d4af37",
        borderRadius: "50%",
        animation: "spin 1s linear infinite"
      }}></div>
    </div>
  );
  
  if (error) return (
    <div style={{ 
      textAlign: "center", 
      padding: "20px", 
      color: "#e74c3c",
      background: "rgba(255, 255, 255, 0.1)",
      backdropFilter: "blur(20px)",
      borderRadius: "15px",
      margin: "40px auto",
      maxWidth: "1140px",
      border: "1px solid rgba(212, 175, 55, 0.2)"
    }}>
      {error}
    </div>
  );

  return (
    <section style={{ 
      background: "rgba(255, 255, 255, 0.05)",
      backdropFilter: "blur(20px)",
      borderTop: "1px solid rgba(212, 175, 55, 0.1)",
      borderBottom: "1px solid rgba(212, 175, 55, 0.1)",
      position: "relative",
      overflow: "hidden"
    }}>
      <div style={{ 
        maxWidth: "1200px", 
        margin: "0 auto",
        padding: "40px 20px"
      }}>
        <div style={{
          textAlign: "center",
          marginBottom: "40px"
        }}>
          <h2 style={{
            fontSize: "2rem",
            fontWeight: "600",
            color: "#2c1810",
            fontFamily: "'Cormorant Garamond', serif",
            margin: "0 0 10px"
          }}>
            Produits Populaires
          </h2>
          
          <div style={{
            width: "80px",
            height: "2px",
            background: "#d4af37",
            margin: "0 auto",
            borderRadius: "1px"
          }} />
        </div>

        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "10px",
          marginBottom: "30px",
          flexWrap: "wrap"
        }}>
          {[
            { mode: "swiper", icon: <FiGrid />, label: "Carousel" },
            { mode: "list", icon: <FiList />, label: "Liste" },
            { mode: "grid", icon: <FiGrid />, label: "Grille", hideOnMobile: true },
            { mode: "table", icon: <FiTable />, label: "Tableau", hideOnMobile: true, disabled: isMobile }
          ].map(({ mode, icon, label, hideOnMobile, disabled }) => {
            if (hideOnMobile && isMobile) {
              return null;
            }
            
            return (
              <button
                key={mode}
                onClick={() => !disabled && setViewMode(mode)}
                disabled={disabled}
                style={{
                  padding: "8px 16px",
                  backgroundColor: viewMode === mode ? "rgba(212, 175, 55, 0.2)" : "rgba(255, 255, 255, 0.1)",
                  color: disabled ? "#95a5a6" : "#2c1810",
                  border: "1px solid rgba(212, 175, 55, 0.2)",
                  borderRadius: "12px",
                  cursor: disabled ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  fontWeight: "500",
                  transition: "all 0.3s ease",
                  backdropFilter: "blur(10px)",
                  opacity: disabled ? 0.5 : 1
                }}
                title={disabled ? "Non disponible sur mobile" : ""}
              >
                {icon}
                {label}
              </button>
            );
          })}
        </div>

        {viewMode === "swiper" && (
          <div style={{
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "20px",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(212, 175, 55, 0.2)",
            overflow: "hidden"
          }}>
            <Swiper
              spaceBetween={isMobile ? 15 : 20}
              slidesPerView={3}
              navigation={{
                nextEl: ".swiper-button-next-popular",
                prevEl: ".swiper-button-prev-popular"
              }}
              modules={[Navigation, Autoplay]}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false
              }}
              breakpoints={{
                320: { slidesPerView: 1, spaceBetween: 10 },
                480: { slidesPerView: 1.2, spaceBetween: 15 },
                640: { slidesPerView: 2, spaceBetween: 15 },
                768: { slidesPerView: 2.2, spaceBetween: 20 },
                1024: { slidesPerView: 3, spaceBetween: 20 }
              }}
              style={{ 
                padding: isMobile ? "15px 5px 35px" : "20px 10px 40px"
              }}
            >
              {products.map((product, i) => (
                <SwiperSlide key={`${product._id}-${i}`}>
                  {renderProductCard(product, i)}
                </SwiperSlide>
              ))}

              <div className="swiper-button-prev-popular" style={{
                position: "absolute",
                left: isMobile ? "5px" : "10px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: "10",
                width: isMobile ? "35px" : "40px",
                height: isMobile ? "35px" : "40px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "1px solid rgba(212, 175, 55, 0.2)",
                backdropFilter: "blur(10px)"
              }}>
                <svg width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="#2c1810" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>

              <div className="swiper-button-next-popular" style={{
                position: "absolute",
                right: isMobile ? "5px" : "10px",
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: "10",
                width: isMobile ? "35px" : "40px",
                height: isMobile ? "35px" : "40px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "1px solid rgba(212, 175, 55, 0.2)",
                backdropFilter: "blur(10px)"
              }}>
                <svg width={isMobile ? "18" : "20"} height={isMobile ? "18" : "20"} viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="#2c1810" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </Swiper>
          </div>
        )}

        {viewMode === "grid" && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "25px",
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "20px",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(212, 175, 55, 0.2)",
            padding: "25px"
          }}>
            {products.map((product, index) => renderProductCard(product, index))}
          </div>
        )}

        {viewMode === "list" && (
          <div style={{
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "20px",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(212, 175, 55, 0.2)",
            padding: "25px"
          }}>
            {products.map((product, index) => renderProductList(product, index))}
          </div>
        )}

        {viewMode === "table" && canShowTable() && (
          <div style={{
            background: "rgba(255, 255, 255, 0.1)",
            borderRadius: "20px",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(212, 175, 55, 0.2)",
            overflow: "auto",
            maxHeight: "600px"
          }}>
            {products.length === 0 ? (
              <div style={{
                padding: "40px",
                textAlign: "center",
                color: "#7f8c8d",
                fontSize: "16px"
              }}>
                Aucun produit populaire disponible
              </div>
            ) : (
              <table style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: isMobile ? "600px" : "auto"
              }}>
                <thead>
                  <tr style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    borderBottom: "1px solid rgba(212, 175, 55, 0.2)",
                    position: "sticky",
                    top: 0,
                    zIndex: 1
                  }}>
                    <th style={{ 
                      padding: "12px", 
                      textAlign: "center",
                      color: "#2c1810",
                      fontWeight: "600",
                      fontSize: "13px",
                      minWidth: "80px"
                    }}>Image</th>
                    <th style={{ 
                      padding: "12px", 
                      textAlign: "left",
                      color: "#2c1810",
                      fontWeight: "600",
                      fontSize: "13px",
                      minWidth: "200px"
                    }}>Nom</th>
                    <th style={{ 
                      padding: "12px", 
                      textAlign: "center",
                      color: "#2c1810",
                      fontWeight: "600",
                      fontSize: "13px",
                      minWidth: "120px"
                    }}>Prix</th>
                    <th style={{ 
                      padding: "12px", 
                      textAlign: "center",
                      color: "#2c1810",
                      fontWeight: "600",
                      fontSize: "13px",
                      minWidth: "100px"
                    }}>Type</th>
                    <th style={{ 
                      padding: "12px", 
                      textAlign: "center",
                      color: "#2c1810",
                      fontWeight: "600",
                      fontSize: "13px",
                      minWidth: "80px"
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => renderProductTable(product, index))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default PopularProducts;